import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Plus, Edit2, Trash2, Star, Eye, EyeOff, RefreshCw, Save, X, ExternalLink, FileText, Sparkles, Globe, FileEdit } from 'lucide-react';
import AutoBlogPanel from './AutoBlogPanel';
import { authFetch } from '../../../utils/authFetch';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyPost = {
  title: '',
  subtitle: '',
  slug: '',
  excerpt: '',
  hero_image_url: '',
  hero_image_alt: '',
  content: '',
  author_display: '1st Ave ICP',
  categories: '',
  tags: '',
  is_featured: false,
  is_published: true,
  publish_date: '',
  seo_title: '',
  seo_description: '',
  voice_search_terms: '',
  embed_html: '',
};

const toListStr = (arr) => Array.isArray(arr) ? arr.join(', ') : (arr || '');
const fromListStr = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);

const BlogAdmin = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | post object
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const authHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  });

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/blog/admin/posts`);
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
      }
    } catch (e) {
      console.error('load posts failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); }, []);

  const openNew = () => {
    setEditing('new');
    setForm(emptyPost);
    setErr(null);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title || '',
      subtitle: p.subtitle || '',
      slug: p.slug || '',
      excerpt: p.excerpt || '',
      hero_image_url: p.hero_image_url || '',
      hero_image_alt: p.hero_image_alt || '',
      content: p.content || '',
      author_display: p.author_display || '1st Ave ICP',
      categories: toListStr(p.categories),
      tags: toListStr(p.tags),
      is_featured: !!p.is_featured,
      is_published: p.is_published !== false,
      publish_date: p.publish_date ? String(p.publish_date).slice(0, 16) : '',
      seo_title: p.seo_title || '',
      seo_description: p.seo_description || '',
      voice_search_terms: toListStr(p.voice_search_terms),
      embed_html: p.embed_html || '',
    });
    setErr(null);
  };

  const close = () => { setEditing(null); setForm(emptyPost); setErr(null); };

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const payload = {
        ...form,
        categories: fromListStr(form.categories),
        tags: fromListStr(form.tags),
        voice_search_terms: fromListStr(form.voice_search_terms),
        publish_date: form.publish_date ? new Date(form.publish_date).toISOString() : null,
        hero_image_url: form.hero_image_url || null,
        hero_image_alt: form.hero_image_alt || null,
        subtitle: form.subtitle || null,
        excerpt: form.excerpt || null,
        seo_title: form.seo_title || null,
        seo_description: form.seo_description || null,
        embed_html: form.embed_html || null,
      };
      if (editing === 'new') {
        const res = await authFetch(`${API}/api/blog/admin/posts`, {
          method: 'POST', body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error((await res.json()).detail || 'Create failed');
      } else {
        const res = await authFetch(`${API}/api/blog/admin/posts/${editing.id}`, {
          method: 'PUT', body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error((await res.json()).detail || 'Update failed');
      }
      await fetchPosts();
      close();
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete post "${p.title}"?`)) return;
    const res = await authFetch(`${API}/api/blog/admin/posts/${p.id}`, {
      method: 'DELETE',
    });
    if (res.ok) fetchPosts();
  };

  const [seeding, setSeeding] = useState(false);
  const seedLongtail = async () => {
    if (!window.confirm('Add ~10 starter longtail-SEO / voice-search blog drafts? They will appear as unpublished drafts for you to edit and publish.')) return;
    setSeeding(true);
    try {
      const res = await authFetch(`${API}/api/blog/admin/seed-longtail`, {
        method: 'POST',
      });
      const data = await res.json();
      if (res.ok) {
        window.alert(`Added ${data.total_created} drafts${data.skipped?.length ? ` (${data.skipped.length} already existed and were skipped)` : ''}. Edit them in this list.`);
        await fetchPosts();
      } else {
        window.alert('Seed failed: ' + (data.detail || res.status));
      }
    } catch (e) {
      window.alert('Seed failed: ' + e.message);
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-6">
      <AutoBlogPanel onPostCreated={fetchPosts} />

      <div className="bg-white rounded-xl shadow-lg p-4 md:p-6" data-testid="blog-admin">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-pink-100 rounded-xl"><FileText className="w-6 h-6 text-[#E91E8C]" /></div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold">Blog / News</h2>
            <p className="text-gray-600 text-sm">Create and manage blog posts for SEO & LLM visibility</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={seedLongtail}
            disabled={seeding}
            variant="outline"
            className="gap-2 border-purple-400 text-purple-700 hover:bg-purple-50"
            data-testid="seed-longtail-btn"
            title="Creates ~10 pre-filled longtail/voice-search blog drafts for you to edit and publish"
          >
            {seeding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {seeding ? 'Seeding…' : 'Seed Longtail SEO Drafts'}
          </Button>
          <Button onClick={openNew} className="bg-[#E91E8C] hover:bg-[#C4197A] gap-2" data-testid="new-post-btn">
            <Plus className="w-4 h-4" /> New Post
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#E91E8C]" /></div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No posts yet. Click <strong>New Post</strong> to create your first one.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Slug</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Published</th>
                <th className="px-3 py-2">Views</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className="border-t" data-testid={`blog-row-${p.slug}`}>
                  <td className="px-3 py-2 font-semibold">
                    {p.is_featured && <Star className="w-4 h-4 text-yellow-500 inline mr-1" />}
                    {p.title}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">/{p.slug}</td>
                  <td className="px-3 py-2">
                    {p.is_published ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs font-bold"><Eye className="w-3 h-3" /> Published</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-gray-500 text-xs font-bold"><EyeOff className="w-3 h-3" /> Draft</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">{p.publish_date ? new Date(p.publish_date).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-2 text-xs">{p.view_count || 0}</td>
                  <td className="px-3 py-2 whitespace-nowrap flex gap-2">
                    {p.is_published && (
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> View
                      </a>
                    )}
                    <button onClick={() => openEdit(p)} className="text-[#E91E8C] hover:underline text-xs inline-flex items-center gap-1" data-testid={`edit-${p.slug}`}>
                      <Edit2 className="w-3 h-3" /> Edit
                    </button>
                    <button onClick={() => remove(p)} className="text-red-600 hover:underline text-xs inline-flex items-center gap-1" data-testid={`delete-${p.slug}`}>
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Editor Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-xl font-bold">{editing === 'new' ? 'New Post' : 'Edit Post'}</h3>
              <button onClick={close} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-4">
              {err && <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg p-3 text-sm">{err}</div>}

              {/* Prominent Publish status banner */}
              <div
                className={`rounded-xl border-2 p-4 flex items-center justify-between gap-3 ${
                  form.is_published
                    ? 'bg-green-50 border-green-300'
                    : 'bg-amber-50 border-amber-300'
                }`}
                data-testid="post-publish-banner"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${form.is_published ? 'bg-green-100' : 'bg-amber-100'}`}>
                    {form.is_published
                      ? <Globe className="w-5 h-5 text-green-700" />
                      : <FileEdit className="w-5 h-5 text-amber-700" />}
                  </div>
                  <div>
                    <div className={`font-bold text-sm ${form.is_published ? 'text-green-800' : 'text-amber-800'}`}>
                      {form.is_published ? 'Live on the site' : 'Draft — not visible to visitors'}
                    </div>
                    <div className="text-xs text-gray-600">
                      {form.is_published
                        ? 'This post is published and appears on /blog.'
                        : 'Toggle to publish, or use the "Publish & Save" button below.'}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, is_published: !form.is_published })}
                  className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-colors ${
                    form.is_published
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-amber-500 hover:bg-amber-600 text-white'
                  }`}
                  data-testid="post-publish-banner-toggle"
                >
                  {form.is_published ? 'Unpublish' : 'Publish'}
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Title *">
                  <input className="inp" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="post-title-input" />
                </Field>
                <Field label="Slug (auto if blank)" hint="URL-safe, e.g. best-ice-cream-texas">
                  <input className="inp font-mono text-sm" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated" />
                </Field>
              </div>

              <Field label="Subtitle">
                <input className="inp" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
              </Field>

              <Field label="Excerpt (card summary + meta description)">
                <textarea className="inp" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
              </Field>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Hero Image URL">
                  <input className="inp" value={form.hero_image_url} onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })} placeholder="https://..." />
                </Field>
                <Field label="Hero Image Alt">
                  <input className="inp" value={form.hero_image_alt} onChange={(e) => setForm({ ...form, hero_image_alt: e.target.value })} />
                </Field>
              </div>

              <Field label="Content (Markdown)" hint="Supports Markdown, links, images, lists, blockquotes, code.">
                <textarea className="inp font-mono text-sm" rows={14} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="post-content-input" />
              </Field>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Categories (comma-separated)">
                  <input className="inp" value={form.categories} onChange={(e) => setForm({ ...form, categories: e.target.value })} placeholder="Recipes, Industry News" />
                </Field>
                <Field label="Tags (comma-separated) — longtail keywords">
                  <input className="inp" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="mineral wells ice cream, vanilla recipe" />
                </Field>
              </div>

              <Field label="Voice Search / LLM Terms (comma-separated natural-language queries)" hint="E.g., 'where can I get homemade ice cream in Mineral Wells'">
                <input className="inp" value={form.voice_search_terms} onChange={(e) => setForm({ ...form, voice_search_terms: e.target.value })} />
              </Field>

              <Field label="Author byline" hint="Defaults to '1st Ave ICP' regardless of logged-in admin">
                <input className="inp" value={form.author_display} onChange={(e) => setForm({ ...form, author_display: e.target.value })} />
              </Field>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="SEO Title (optional override)">
                  <input className="inp" value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} />
                </Field>
                <Field label="Publish Date">
                  <input type="datetime-local" className="inp" value={form.publish_date} onChange={(e) => setForm({ ...form, publish_date: e.target.value })} />
                </Field>
              </div>

              <Field label="SEO Description (optional override)">
                <textarea className="inp" rows={2} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} />
              </Field>

              <Field label="Embed HTML (TikTok / Instagram)" hint="Paste the full blockquote embed code from TikTok or Instagram.">
                <textarea className="inp font-mono text-xs" rows={4} value={form.embed_html} onChange={(e) => setForm({ ...form, embed_html: e.target.value })} />
              </Field>

              <div className="flex flex-wrap gap-6">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} data-testid="post-published-checkbox" />
                  <span className="text-sm font-bold">Published</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} data-testid="post-featured-checkbox" />
                  <span className="text-sm font-bold">Featured (only one at a time)</span>
                </label>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center justify-between gap-3">
              <div className="text-xs text-gray-500 hidden sm:block">
                {form.is_published
                  ? 'Saving will keep this post live.'
                  : 'Use "Publish & Save" to make this post live immediately.'}
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={close}>Cancel</Button>
                {!form.is_published && (
                  <Button
                    onClick={() => { setForm({ ...form, is_published: true }); setTimeout(save, 0); }}
                    disabled={saving || !form.title.trim()}
                    className="bg-green-600 hover:bg-green-700 text-white gap-2"
                    data-testid="publish-and-save-btn"
                  >
                    <Globe className="w-4 h-4" />
                    Publish & Save
                  </Button>
                )}
                <Button onClick={save} disabled={saving || !form.title.trim()} className="bg-[#E91E8C] hover:bg-[#C4197A] gap-2" data-testid="save-post-btn">
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? 'Saving…' : (form.is_published ? 'Save' : 'Save Draft')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`.inp{width:100%;border:2px solid #e5e7eb;border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.875rem;transition:border-color .15s;background:#fff;}.inp:focus{outline:none;border-color:#E91E8C;}`}</style>
      </div>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="block text-sm font-bold text-gray-700 mb-1">{label}</span>
    {children}
    {hint && <span className="block text-xs text-gray-500 mt-1">{hint}</span>}
  </label>
);

export default BlogAdmin;
